import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Grid, IconButton, Paper, Typography,
} from '@material-ui/core';
import type { AxiosError } from 'axios';
import axios from 'axios';
import useSWR from 'swr';
import { useSnackbar } from 'notistack';
import type { GridColDef } from '@material-ui/data-grid';
import { DataGrid } from '@material-ui/data-grid';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import CompanyActionMenu from './CompanyActionMenu';
import globalUseStyles from '../../../globalClasses';
import type { ITag } from '../store/StoreInterfaces';

interface ICompanyInfo {
  companyId: string;
  name: string;
  city: string;
  state: string;
  zipCode: string;
  tags: Array<ITag>;
}

const AllCompanies: React.FC = () => {
  const navigate = useNavigate();
  const globalStyles = globalUseStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [companyId, setCompanyId] = React.useState<string | null>(null);

  const companyFetcher = (url: string) => axios.get<Array<ICompanyInfo>>(url)
    .then((response) => response.data)
    .catch((e: AxiosError) => {
      if (e.response?.status === 404) {
        enqueueSnackbar(`There was the following error when fetching the companies: ${JSON.stringify(e.response.data)}`, { persist: false, variant: 'error' });
      } else {
        enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
      }
    });

  const { data } = useSWR(
    ['/company/getCompanies'],
    (url) => companyFetcher(url),
  );

  const handleAddCompanyBtnClicked = () => {
    navigate('/company');
  };

  const handleActionMenuClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    if (!anchorEl) {
      setCompanyId(id);
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setCompanyId(null);
    setAnchorEl(null);
  };

  const displayVertIcon = (companyIdParameter: string) => (
    <IconButton
      aria-label="more"
      aria-controls="long-menu"
      aria-haspopup="true"
      id="table-kabob"
      onClick={(e) => handleActionMenuClick(e, companyIdParameter)}
    >
      <MoreVertIcon />
    </IconButton>
  );

  const manualAddColumns: GridColDef[] = [
    { field: 'name', headerName: 'Company Name', flex: 1 },
    { field: 'city', headerName: 'City', flex: 1 },
    { field: 'state', headerName: 'State', flex: 1 },
    { field: 'zipCode', headerName: 'ZipCode', flex: 0.5 },
    {
      field: 'tags',
      headerName: 'Tags',
      flex: 1.5,
      renderCell: ({ value }) => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const tagArray = (value as ITag[]).map((tag) => `${tag.key}:${tag.value}`);
        return tagArray.join(', ');
      },
    },
    {
      field: 'companyId',
      type: 'string',
      headerName: 'Actions',
      flex: 0.5,
      renderCell: ({ value }) => (
        ((typeof (value) === 'string')
          ? displayVertIcon(value)
          : <></>
        )
      ),
    },
  ];

  return (
    <Grid>
      <Grid container>
        <Grid item xs={6}>
          <Box pb={3} pt={3}>
            <Typography variant="h4" className={globalStyles.labelText}>{'Companies'}</Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Grid container direction="row-reverse">
            <Button color="primary" variant="contained" onClick={handleAddCompanyBtnClicked}>{'ADD COMPANY'}</Button>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Box pb={3}>
          <Paper elevation={4}>
            <Box p={3}>
              <Typography variant="h6">{'Companies'}</Typography>
            </Box>
            {data
              && (
                <>
                  <DataGrid
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    getRowId={(row) => ((row as ICompanyInfo).companyId)}
                    rows={data}
                    columns={manualAddColumns}
                    autoHeight
                    checkboxSelection
                    className={globalStyles.transparentDataGrid}
                  />
                  {
                    companyId && anchorEl && (
                      <CompanyActionMenu
                        companyId={companyId}
                        anchorEl={anchorEl}
                        handleClose={handleClose}
                      />
                    )
                  }
                </>
              )}
          </Paper>
        </Box>
      </Grid>
      <Grid container xs={12} direction="row-reverse">
        <Grid item>
          <Box pt={3}>
            <Button color="primary" variant="text" onClick={() => { Error('Not implemented'); }}>{'BULK IMPORT COMPANIES'}</Button>
            <Button color="primary" variant="contained" onClick={handleAddCompanyBtnClicked}>{'ADD COMPANY'}</Button>
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AllCompanies;
